/**
 * One vendor: what they are, drafted from the books, for a person to confirm or correct.
 * Used by Accounting > Vendors (the review queue) and by the bill screen (the same card
 * in a popup), so there is one screen to learn and one place a correction is made.
 *
 * Renders what lib/vendor-profiles.js computed and derives nothing. Every figure on the
 * card is from QuickBooks; the only thing Claude wrote is the "what they sell us" line,
 * and the card says where that line came from until a person has confirmed it.
 */
import React, { useState } from 'react';
import { Check, Pencil, ChevronDown, ChevronRight } from 'lucide-react';
import { apVendorProfiles } from '../services/api';
import { VendorFacts } from './BuyFacts';
import { apBuyFacts } from '../services/api';

const money = (v) => (v == null || !isFinite(Number(v)) ? '—'
  : '$' + Number(v).toLocaleString('en-US', { maximumFractionDigits: 0 }));
const money2 = (v) => (v == null || !isFinite(Number(v)) ? '—'
  : '$' + Number(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
const fmtDay = (d) => {
  if (!d) return '—';
  const [y, m, day] = String(d).slice(0, 10).split('-').map(Number);
  return new Date(y, m - 1, day).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const k = { fontSize: 12.5, color: 'var(--gray-600, #4b5563)', width: 150, flexShrink: 0 };
const v = { fontSize: 13.5, color: 'var(--gray-900, #111827)', flex: 1, minWidth: 0 };
const line = { display: 'flex', gap: 12, padding: '4px 0', alignItems: 'baseline' };
const pill = (bg, fg) => ({
  display: 'inline-block', fontSize: 11, fontWeight: 700, borderRadius: 10, padding: '2px 8px',
  background: bg, color: fg, whiteSpace: 'nowrap',
});

const SOURCE_NOTE = {
  accounts: 'Taken from the account names; nobody has worded it yet.',
  claude: 'Drafted by Claude from their bills.',
};

export default function VendorProfileCard({ vendor: initial, onChanged }) {
  const [vendor, setVendor] = useState(initial);
  const [mode, setMode] = useState('view');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [bills, setBills] = useState(null);
  const [showBills, setShowBills] = useState(false);
  const [form, setForm] = useState({});
  const [note, setNote] = useState('');

  // The parent hands down a fresh row after a reload; follow it unless mid-edit.
  React.useEffect(() => { if (mode === 'view') setVendor(initial); }, [initial]); // eslint-disable-line react-hooks/exhaustive-deps

  const apply = (out) => {
    if (out && out.vendor) { setVendor(out.vendor); if (onChanged) onChanged(out.vendor); }
  };
  const run = async (fn) => {
    setBusy(true); setErr('');
    try { await fn(); } catch (e) { setErr(e.message || 'That did not save.'); }
    setBusy(false);
  };

  const confirm = () => run(async () => {
    apply(await apVendorProfiles('review', { key: vendor.key, name: vendor.name, what: vendor.what }));
  });
  const unconfirm = () => run(async () => {
    apply(await apVendorProfiles('review', { key: vendor.key, name: vendor.name, undo: true }));
  });
  const startFix = () => {
    setForm({
      what: vendor.what || '',
      soleSupplier: !vendor.soleSupplierSet ? '' : vendor.soleSupplier ? 'yes' : 'no',
      // Only what a person SET goes into the boxes. The computed figures are shown as
      // the placeholder, so saving an untouched form does not freeze today's statistics.
      rangeLow: vendor.range && vendor.range.set && vendor.range.low != null ? String(vendor.range.low) : '',
      rangeHigh: vendor.range && vendor.range.set && vendor.range.high != null ? String(vendor.range.high) : '',
      cadenceDays: vendor.cadenceSet && vendor.cadenceDays != null ? String(vendor.cadenceDays) : '',
    });
    setNote(''); setErr(''); setMode('fix');
  };
  const saveFix = () => run(async () => {
    // One line said in plain words goes first: it is recorded as said, and Claude rewords
    // the description from it. The boxes are then saved over the top, so anything typed
    // into a box wins over the rewording.
    let latest = null;
    if (note.trim()) {
      latest = await apVendorProfiles('tell', { key: vendor.key, name: vendor.name, note: note.trim() });
    }
    const whatTyped = form.what.trim() !== (vendor.what || '').trim();
    const out = await apVendorProfiles('save', {
      key: vendor.key, name: vendor.name,
      fields: {
        what: whatTyped || !latest ? form.what : '',
        soleSupplier: form.soleSupplier === 'yes' ? true : form.soleSupplier === 'no' ? false
          : (latest && latest.vendor && latest.vendor.soleSupplierSet ? latest.vendor.soleSupplier : null),
        rangeLow: form.rangeLow, rangeHigh: form.rangeHigh, cadenceDays: form.cadenceDays,
      },
    });
    apply(out);
    setMode('view');
  });
  const toggleBills = async () => {
    const next = !showBills;
    setShowBills(next);
    if (next && !bills) {
      try { setBills((await apVendorProfiles('recent', { key: vendor.key })).bills || []); }
      catch (e) { setBills([]); setErr(e.message); }
    }
  };

  const terms = (vendor.facts || []).filter((f) => f.kind === 'terms');
  const r = vendor.range || {};
  const sole = vendor.soleSupplier;
  const spell = (vendor.spellings || []).filter((s) => s && s !== vendor.name);

  return (
    <div className="card" style={{
      padding: 16, marginBottom: 12,
      borderLeft: `4px solid ${vendor.reviewed ? '#1c7c43' : 'var(--orange, #DC582A)'}`,
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'baseline', flexWrap: 'wrap' }}>
        <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--navy, #00395C)' }}>
          {vendor.name}{' '}
          {vendor.reviewed
            ? <span style={pill('#e8f5ec', '#1c7c43')}>Confirmed{vendor.reviewedByName ? ` by ${vendor.reviewedByName.split(' ')[0]}` : ''}</span>
            : <span style={pill('#fff3e6', '#9a4a1c')}>Not yet confirmed</span>}
        </div>
        <div style={{ fontSize: 13, color: 'var(--gray-600, #4b5563)' }}>
          {vendor.bills > 0 ? `${money(vendor.spent)} over two years · ${vendor.bills} bills` : 'Nothing in QuickBooks in two years'}
        </div>
      </div>

      {mode === 'view' ? (
        <div style={{ marginTop: 10 }}>
          <div style={line}>
            <span style={k}>What they sell us</span>
            <span style={v}>
              <strong>{vendor.what || 'Not described yet'}</strong>
              {!vendor.reviewed && SOURCE_NOTE[vendor.whatSource] && (
                <span style={{ fontSize: 11.5, color: 'var(--gray-500, #6b7280)', marginLeft: 8 }}>
                  {SOURCE_NOTE[vendor.whatSource]}
                </span>
              )}
            </span>
          </div>
          <div style={line}>
            <span style={k}>Posts to</span>
            <span style={v}>
              {(vendor.accounts || []).length === 0 ? '—' : vendor.accounts.slice(0, 4).map((a) => (
                <div key={a.account}>{a.label} <span style={{ color: 'var(--gray-500, #6b7280)', fontSize: 12 }}>
                  · {a.bills} bills · {money(a.spent)}</span></div>
              ))}
            </span>
          </div>
          <div style={line}>
            <span style={k}>Bills</span>
            <span style={v}>
              {vendor.cadence || '—'}
              {r.low != null && r.high != null ? ` · most run ${money(r.low)} to ${money(r.high)}` : ''}
              {r.set ? <span style={{ fontSize: 11.5, color: 'var(--gray-500, #6b7280)' }}> (range set by hand)</span> : ''}
              {vendor.lastBill ? ` · last ${fmtDay(vendor.lastBill)}` : ''}
            </span>
          </div>
          <div style={line}>
            <span style={k}>Only supplier of this</span>
            <span style={v}>{sole === true ? 'Yes' : sole === false ? 'No' : 'Not sure'}</span>
          </div>
          {spell.length > 0 && (
            <div style={line}>
              <span style={k}>In QuickBooks as</span>
              <span style={{ ...v, fontSize: 12.5, color: 'var(--gray-600, #4b5563)' }}>{spell.join(' · ')}</span>
            </div>
          )}
          {/* Payment terms get their own line (Greg, 2026-10-09). A saved term is a fact
              somebody confirmed; what their bills in Approvals print is only offered. */}
          <div style={line}>
            <span style={k}>Payment terms</span>
            <span style={v}>
              {terms.length > 0 ? terms.map((f) => (
                <div key={f.id}>
                  <strong>{f.fact}</strong>
                  <span style={{ fontSize: 11.5, color: 'var(--gray-500, #6b7280)' }}>
                    {' '}{f.by ? f.by.split(' ')[0] : ''}{f.at ? `, ${fmtDay(f.at)}` : ''}
                  </span>
                </div>
              )) : vendor.termsSeen ? (
                <span>
                  {vendor.termsSeen.terms} on {vendor.termsSeen.bills} bill{vendor.termsSeen.bills === 1 ? '' : 's'} in Approvals
                  {vendor.termsSeen.mixed ? ' (their bills do not all agree)' : ''}{' '}
                  <button className="btn btn-ghost" style={{ padding: 0 }} disabled={busy}
                    onClick={() => run(async () => {
                      await apBuyFacts('add', { kind: 'terms', vendor: vendor.name, fact: vendor.termsSeen.terms });
                      apply(await apVendorProfiles('get', { key: vendor.key }));
                    })}>
                    Save As Their Terms
                  </button>
                </span>
              ) : <span style={{ color: 'var(--gray-500, #6b7280)' }}>Not saved yet. Add it under Before ordering.</span>}
            </span>
          </div>
          <div style={line}>
            <span style={k}>Before ordering</span>
            <span style={v}>
              <VendorFacts vendorName={vendor.name} facts={(vendor.facts || []).filter((f) => f.kind !== 'terms')}
                onChanged={async () => apply(await apVendorProfiles('get', { key: vendor.key }))} />
            </span>
          </div>
          {(vendor.notes || []).length > 0 && (
            <div style={line}>
              <span style={k}>What people said</span>
              <span style={v}>
                {vendor.notes.slice(-3).map((n, i) => (
                  <div key={i} style={{ fontSize: 12.5 }}>
                    “{n.text}” <span style={{ color: 'var(--gray-500, #6b7280)' }}>
                      {n.byName ? n.byName.split(' ')[0] : ''}{n.at ? `, ${fmtDay(n.at)}` : ''}</span>
                  </div>
                ))}
              </span>
            </div>
          )}
        </div>
      ) : (
        <div style={{ marginTop: 12, display: 'grid', gap: 10 }}>
          <div>
            <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 4 }}>Tell me in a line what is wrong (optional)</div>
            <textarea className="form-input" rows={2} value={note} onChange={(e) => setNote(e.target.value)}
              placeholder="e.g. This is our gas supplier for the steamers and the office, nobody else sells us propane"
              style={{ width: '100%', resize: 'vertical' }} />
            <div style={{ fontSize: 11.5, color: 'var(--gray-500, #6b7280)', marginTop: 2 }}>
              It is kept as you wrote it, and the description is reworded from it.
            </div>
          </div>
          <div>
            <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 4 }}>What they sell us</div>
            <input className="form-input" value={form.what} style={{ width: '100%' }}
              onChange={(e) => setForm({ ...form, what: e.target.value })} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 10 }}>
            <div>
              <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 4 }}>Only supplier of this</div>
              <select className="form-input" value={form.soleSupplier} style={{ width: '100%' }}
                onChange={(e) => setForm({ ...form, soleSupplier: e.target.value })}>
                <option value="">Let the books decide</option>
                <option value="yes">Yes</option>
                <option value="no">No</option>
              </select>
            </div>
            <div>
              <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 4 }}>Normal bill, low end ($)</div>
              <input className="form-input" inputMode="decimal" value={form.rangeLow} style={{ width: '100%' }}
                placeholder={r.low != null ? String(Math.round(r.low)) : ''}
                onChange={(e) => setForm({ ...form, rangeLow: e.target.value })} />
            </div>
            <div>
              <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 4 }}>Normal bill, high end ($)</div>
              <input className="form-input" inputMode="decimal" value={form.rangeHigh} style={{ width: '100%' }}
                placeholder={r.high != null ? String(Math.round(r.high)) : ''}
                onChange={(e) => setForm({ ...form, rangeHigh: e.target.value })} />
            </div>
            <div>
              <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 4 }}>Days between bills</div>
              <input className="form-input" inputMode="numeric" value={form.cadenceDays} style={{ width: '100%' }}
                placeholder={vendor.cadenceDays != null ? String(vendor.cadenceDays) : ''}
                onChange={(e) => setForm({ ...form, cadenceDays: e.target.value })} />
            </div>
          </div>
          <div style={{ fontSize: 11.5, color: 'var(--gray-500, #6b7280)' }}>
            Leave a box empty to keep using what the books show (the grey figure).
          </div>
        </div>
      )}

      {err && <div style={{ color: '#b42318', fontSize: 12.5, marginTop: 8 }}>{err}</div>}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 12, gap: 8, flexWrap: 'wrap' }}>
        <button className="btn btn-ghost" style={{ padding: 0 }} onClick={toggleBills} disabled={vendor.bills === 0}>
          {showBills ? <ChevronDown size={13} /> : <ChevronRight size={13} />} {showBills ? 'Hide' : 'Show'} their most recent bills
        </button>
        {mode === 'view' ? (
          <div style={{ display: 'flex', gap: 8 }}>
            {vendor.reviewed ? (
              <button className="btn btn-ghost" onClick={unconfirm} disabled={busy}>Mark Not Confirmed</button>
            ) : (
              <button className="btn btn-primary" onClick={confirm} disabled={busy}>
                <Check size={14} /> Looks Right
              </button>
            )}
            <button className="btn btn-outline" onClick={startFix} disabled={busy}>
              <Pencil size={13} /> Fix It
            </button>
          </div>
        ) : (
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn-outline" onClick={() => setMode('view')} disabled={busy}>Cancel</button>
            <button className="btn btn-primary" onClick={saveFix} disabled={busy}>{busy ? 'Saving…' : 'Save And Confirm'}</button>
          </div>
        )}
      </div>

      {showBills && (
        <div style={{ marginTop: 10, borderTop: '1px solid var(--gray-200, #e5e7eb)', paddingTop: 8 }}>
          {!bills ? (
            <div style={{ fontSize: 12.5, color: 'var(--gray-500, #6b7280)' }}>Reading QuickBooks…</div>
          ) : bills.length === 0 ? (
            <div style={{ fontSize: 12.5, color: 'var(--gray-500, #6b7280)' }}>No bills in the last two years.</div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
              <tbody>
                {bills.map((b, i) => (
                  <tr key={i} style={{ borderTop: i ? '1px solid var(--gray-100, #f3f4f6)' : 'none' }}>
                    <td style={{ padding: '5px 6px', whiteSpace: 'nowrap' }}>{fmtDay(b.date)}</td>
                    <td style={{ padding: '5px 6px', whiteSpace: 'nowrap', textAlign: 'right', fontWeight: 600 }}>{money2(b.amount)}</td>
                    <td style={{ padding: '5px 6px' }}>{(b.accounts || []).join(' · ')}</td>
                    <td style={{ padding: '5px 6px', color: 'var(--gray-600, #4b5563)' }}>{b.memo || ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}
